import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import { useCurrency } from '@/context/RegionContext';
import SEO from '@/components/SEO';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { User, Heart, Package, MapPin, LogOut, Printer, ShoppingCart, Trash2, Edit, Save, X, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TABS = [
  { key: 'orders', label: 'My Orders', icon: Package },
  { key: 'wishlist', label: 'Wishlist', icon: Heart },
  { key: 'profile', label: 'Edit Profile', icon: Edit },
];

const formatDate = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '';

const Profile = () => {
  const { user, logout, login, refreshUser } = useAuth();
  const { formatPrice } = useCurrency();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('orders');
  const [orders, setOrders] = useState([]);
  const [wishlist, setWishlist] = useState([]);
  const [loadingOrders, setLoadingOrders] = useState(false);
  const [loadingWishlist, setLoadingWishlist] = useState(false);
  const [saving, setSaving] = useState(false);
  const [profileForm, setProfileForm] = useState({ name: '', phone: '', address_line1: '', address_line2: '', city: '', state: '', pincode: '', country: 'India' });

  useEffect(() => {
    if (user) {
      setProfileForm({
        name: user.name || '',
        phone: user.phone || '',
        address_line1: user.shipping_address?.address_line1 || '',
        address_line2: user.shipping_address?.address_line2 || '',
        city: user.shipping_address?.city || '',
        state: user.shipping_address?.state || '',
        pincode: user.shipping_address?.pincode || '',
        country: user.shipping_address?.country || 'India',
      });
    }
  }, [user]);

  useEffect(() => {
    if (!user) return;
    if (activeTab === 'orders') fetchOrders();
    if (activeTab === 'wishlist') fetchWishlist();
  }, [activeTab, user]);

  const fetchOrders = async () => {
    setLoadingOrders(true);
    try {
      const res = await axios.get(`${API}/customer/orders`, { withCredentials: true });
      setOrders(res.data.orders || []);
    } catch { /* ignore */ }
    finally { setLoadingOrders(false); }
  };

  const fetchWishlist = async () => {
    setLoadingWishlist(true);
    try {
      const res = await axios.get(`${API}/wishlist`, { withCredentials: true });
      setWishlist(res.data.items || []);
    } catch { /* ignore */ }
    finally { setLoadingWishlist(false); }
  };

  const removeFromWishlist = async (artworkId) => {
    try {
      await axios.delete(`${API}/wishlist/${artworkId}`, { withCredentials: true });
      setWishlist(prev => prev.filter(a => a.id !== artworkId));
      await refreshUser();
      toast.success('Removed from wishlist');
    } catch { toast.error('Failed to remove'); }
  };

  const handleSaveProfile = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/auth/profile`, {
        name: profileForm.name,
        phone: profileForm.phone,
        shipping_address: {
          address_line1: profileForm.address_line1,
          address_line2: profileForm.address_line2,
          city: profileForm.city,
          state: profileForm.state,
          pincode: profileForm.pincode,
          country: profileForm.country,
        },
      }, { withCredentials: true });
      await refreshUser();
      toast.success('Profile updated');
    } catch { toast.error('Failed to update'); }
    finally { setSaving(false); }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
    toast.success('Signed out');
  };

  // Not logged in — show login prompt
  if (!user) {
    return (
      <div className="min-h-screen pt-24 sm:pt-32 pb-16 flex items-center justify-center" data-testid="profile-login-prompt">
        <SEO title="My Account - Gangadhar Som" noindex />
        <div className="text-center max-w-sm mx-auto px-4">
          <User size={48} className="mx-auto mb-4 text-muted-foreground" />
          <h1 className="font-serif text-3xl font-light mb-3">My Account</h1>
          <p className="font-sans text-sm text-muted-foreground mb-6">Sign in to view your orders, wishlist, and manage your profile.</p>
          <Button onClick={login} className="w-full bg-accent hover:bg-accent/90" data-testid="profile-login-btn">
            <svg viewBox="0 0 24 24" className="w-5 h-5 mr-2" fill="currentColor">
              <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
              <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
              <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
              <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
            </svg>
            Sign in with Google
          </Button>
        </div>
      </div>
    );
  }

  const STATUS_COLORS = {
    pending: 'bg-amber-100 text-amber-800',
    confirmed: 'bg-blue-100 text-blue-800',
    processing: 'bg-indigo-100 text-indigo-800',
    shipped: 'bg-violet-100 text-violet-800',
    delivered: 'bg-green-100 text-green-800',
    completed: 'bg-green-100 text-green-800',
  };

  return (
    <div className="min-h-screen pt-24 sm:pt-32 pb-16 sm:pb-24" data-testid="profile-page">
      <SEO title="My Account - Gangadhar Som" noindex />
      <div className="max-w-screen-lg mx-auto px-4 sm:px-6">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          {user.picture ? (
            <img src={user.picture} alt="" className="w-14 h-14 rounded-full border-2 border-accent" data-testid="profile-avatar" />
          ) : (
            <div className="w-14 h-14 rounded-full bg-accent/10 flex items-center justify-center">
              <User size={24} className="text-accent" />
            </div>
          )}
          <div className="flex-1">
            <h1 className="font-serif text-2xl sm:text-3xl font-light" data-testid="profile-name">{user.name}</h1>
            <p className="font-sans text-sm text-muted-foreground" data-testid="profile-email">{user.email}</p>
          </div>
          <Button variant="outline" size="sm" onClick={handleLogout} data-testid="profile-logout-btn">
            <LogOut size={14} className="mr-1.5" /> Sign Out
          </Button>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 mb-8 border-b border-border">
          {TABS.map(({ key, label, icon: Icon }) => (
            <button
              key={key}
              onClick={() => setActiveTab(key)}
              data-testid={`profile-tab-${key}`}
              className={`flex items-center gap-2 px-4 py-3 font-sans text-sm transition-colors border-b-2 ${
                activeTab === key ? 'border-accent text-accent font-medium' : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              <Icon size={16} /> {label}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        {activeTab === 'orders' && (
          <div data-testid="profile-orders-tab">
            {loadingOrders ? (
              <div className="text-center py-12"><Loader2 className="animate-spin mx-auto" /></div>
            ) : orders.length === 0 ? (
              <div className="text-center py-16">
                <Package size={48} className="mx-auto mb-4 text-muted-foreground" />
                <p className="font-serif text-xl mb-2">No orders yet</p>
                <p className="text-sm text-muted-foreground mb-6">Your purchases will appear here.</p>
                <Link to="/gallery"><Button variant="outline">Browse Gallery</Button></Link>
              </div>
            ) : (
              <div className="space-y-4">
                {orders.map((order) => (
                  <div key={order.order_number || order.id} className="bg-card border border-border rounded-lg p-5" data-testid="order-card">
                    <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
                      <div>
                        <p className="font-sans text-xs text-muted-foreground">{formatDate(order.created_at)}</p>
                        <p className="font-serif text-lg font-light">{order.order_number}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className={`px-2.5 py-0.5 rounded-full text-xs font-sans font-medium ${STATUS_COLORS[order.fulfillment_status] || STATUS_COLORS[order.order_status] || 'bg-gray-100 text-gray-800'}`}>
                          {order.fulfillment_status || order.order_status || 'pending'}
                        </span>
                        <span className="font-serif text-lg text-accent">{order.currency === 'USD' ? '$' : '₹'}{(order.total || 0).toLocaleString()}</span>
                      </div>
                    </div>
                    <div className="space-y-2">
                      {(order.items || []).slice(0, 3).map((item, i) => (
                        <div key={i} className="flex items-center gap-3">
                          <div className="flex-1">
                            <p className="font-sans text-sm">{item.title}</p>
                            {item.size_label && <p className="text-xs text-muted-foreground">{item.size_label}</p>}
                          </div>
                        </div>
                      ))}
                      {(order.items || []).length > 3 && (
                        <p className="text-xs text-muted-foreground">+{order.items.length - 3} more items</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'wishlist' && (
          <div data-testid="profile-wishlist-tab">
            {loadingWishlist ? (
              <div className="text-center py-12"><Loader2 className="animate-spin mx-auto" /></div>
            ) : wishlist.length === 0 ? (
              <div className="text-center py-16">
                <Heart size={48} className="mx-auto mb-4 text-muted-foreground" />
                <p className="font-serif text-xl mb-2">Your wishlist is empty</p>
                <p className="text-sm text-muted-foreground mb-6">Heart artworks you love to save them here.</p>
                <Link to="/gallery"><Button variant="outline">Browse Gallery</Button></Link>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {wishlist.map((artwork) => (
                  <div key={artwork.id} className="bg-card border border-border rounded-lg overflow-hidden group" data-testid="wishlist-card">
                    <div className="aspect-[4/3] relative overflow-hidden">
                      <img src={artwork.image_url} alt={artwork.title} className="w-full h-full object-cover" />
                    </div>
                    <div className="p-4">
                      <h3 className="font-serif text-lg font-light mb-1">{artwork.title}</h3>
                      <p className="font-serif text-accent mb-3">{formatPrice(artwork.price || 10000)}</p>
                      <div className="flex gap-2">
                        <Link to={`/print/${artwork.id}`} className="flex-1">
                          <Button variant="outline" size="sm" className="w-full text-xs">
                            <Printer size={12} className="mr-1" /> Print
                          </Button>
                        </Link>
                        <Button variant="outline" size="sm" onClick={() => removeFromWishlist(artwork.id)}
                          className="text-red-500 hover:bg-red-50 hover:text-red-600" data-testid="wishlist-remove-btn">
                          <Trash2 size={12} />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'profile' && (
          <div className="max-w-lg" data-testid="profile-edit-tab">
            <div className="space-y-5">
              <div>
                <Label htmlFor="profile-name">Full Name</Label>
                <Input id="profile-name" data-testid="profile-name-input" value={profileForm.name}
                  onChange={(e) => setProfileForm(p => ({ ...p, name: e.target.value }))} />
              </div>
              <div>
                <Label htmlFor="profile-email">Email</Label>
                <Input id="profile-email" value={user.email} disabled className="opacity-60" />
                <p className="text-xs text-muted-foreground mt-1">Email is linked to your Google account and cannot be changed.</p>
              </div>
              <div>
                <Label htmlFor="profile-phone">Phone</Label>
                <Input id="profile-phone" data-testid="profile-phone-input" value={profileForm.phone}
                  onChange={(e) => setProfileForm(p => ({ ...p, phone: e.target.value }))} placeholder="+91 9876543210" />
              </div>

              <div className="pt-4 border-t border-border">
                <h3 className="font-serif text-lg font-light mb-3 flex items-center gap-2">
                  <MapPin size={16} /> Default Shipping Address
                </h3>
                <div className="space-y-3">
                  <div>
                    <Label>Address Line 1</Label>
                    <Input data-testid="profile-address1-input" value={profileForm.address_line1}
                      onChange={(e) => setProfileForm(p => ({ ...p, address_line1: e.target.value }))} />
                  </div>
                  <div>
                    <Label>Address Line 2</Label>
                    <Input value={profileForm.address_line2}
                      onChange={(e) => setProfileForm(p => ({ ...p, address_line2: e.target.value }))} />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label>City</Label>
                      <Input data-testid="profile-city-input" value={profileForm.city}
                        onChange={(e) => setProfileForm(p => ({ ...p, city: e.target.value }))} />
                    </div>
                    <div>
                      <Label>State</Label>
                      <Input value={profileForm.state}
                        onChange={(e) => setProfileForm(p => ({ ...p, state: e.target.value }))} />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label>Pincode</Label>
                      <Input data-testid="profile-pincode-input" value={profileForm.pincode}
                        onChange={(e) => setProfileForm(p => ({ ...p, pincode: e.target.value }))} />
                    </div>
                    <div>
                      <Label>Country</Label>
                      <Input value={profileForm.country}
                        onChange={(e) => setProfileForm(p => ({ ...p, country: e.target.value }))} />
                    </div>
                  </div>
                </div>
              </div>

              <Button onClick={handleSaveProfile} disabled={saving} className="bg-accent hover:bg-accent/90" data-testid="profile-save-btn">
                {saving ? <Loader2 size={14} className="mr-2 animate-spin" /> : <Save size={14} className="mr-2" />}
                Save Changes
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Profile;
